import { Code2, Smartphone, Sparkles, Compass, ArrowUpRight } from "lucide-react";
import useScrollReveal, { useStaggerReveal } from "@/hooks/useScrollReveal";

const SOFTWARE_IMG =
  "https://images.unsplash.com/photo-1753998941587-5befe71f4572?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxzb2Z0d2FyZSUyMGNvZGUlMjBvbiUyMHNjcmVlbnxlbnwwfHx8fDE3Nzc0ODIyMzZ8MA&ixlib=rb-4.1.0&q=85";
const MOBILE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/9cc71183-e08c-4a6f-a9e5-3cc8fb0af69f/images/d66506ced29a399aec59de651eb41e9aca9370e7a45c9e071fde98f70987e870.png";

const SERVICES = [
  {
    n: "01",
    icon: Code2,
    title: "Desarrollo de Software a Medida",
    desc: "Plataformas web, sistemas empresariales y arquitecturas escalables diseñadas para optimizar tus procesos operativos.",
    bullets: ["SaaS B2B", "ERP & CRM", "APIs & micro-servicios"],
    image: SOFTWARE_IMG,
  },
  {
    n: "02",
    icon: Smartphone,
    title: "Aplicaciones Móviles",
    desc: "Apps nativas (iOS / Android), PWA y soluciones híbridas enfocadas en rendimiento excepcional y UX premium.",
    bullets: ["React Native", "Swift / Kotlin", "Diseño UX premium"],
    image: MOBILE_IMG,
  },
  {
    n: "03",
    icon: Sparkles,
    title: "Soluciones de IA",
    desc: "Integración de inteligencia artificial, machine learning y automatización para decisiones basadas en datos.",
    bullets: ["LLMs & RAG", "Automatización", "Modelos predictivos"],
  },
  {
    n: "04",
    icon: Compass,
    title: "Consultoría Tecnológica",
    desc: "Auditorías de arquitectura, diseño UX/UI y estrategias de transformación digital para empresas B2B.",
    bullets: ["Roadmaps técnicos", "Auditoría de código", "Diseño UX/UI"],
  },
];

export default function Services() {
  const headerRef = useScrollReveal();
  const gridRef = useStaggerReveal({ delayStep: 100 });
  return (
    <section
      id="servicios"
      data-testid="services-section"
      className="bg-white border-t border-[#E5E5E5]"
    >
      <div className="px-6 md:px-12 lg:px-16 max-w-[1440px] mx-auto py-24 md:py-32">
        <div ref={headerRef} className="reveal-on-scroll grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 md:mb-20">
          <div className="lg:col-span-5">
            <span className="eyebrow">[ 02 ] Servicios</span>
            <h2 className="display-2 mt-5">
              Nuestra oferta tecnológica.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-[#525252] text-base md:text-lg leading-relaxed">
              Cuatro disciplinas, un solo equipo. Combinamos ingeniería, diseño y
              ciencia de datos para construir productos digitales que tu negocio
              realmente necesita.
            </p>
          </div>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <article
                key={s.n}
                data-testid={`service-card-${s.n}`}
                data-stagger-item
                className={`swiss-card p-8 md:p-10 flex flex-col ${
                  i === 0 || i === 3 ? "md:row-span-1" : ""
                }`}
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#A3A3A3]">
                    [ {s.n} ]
                  </span>
                  <div className="w-11 h-11 border border-[#E5E5E5] flex items-center justify-center">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                </div>

                <h3 className="font-display text-2xl md:text-[28px] font-bold tracking-tight leading-tight mb-4">
                  {s.title}
                </h3>
                <p className="text-[#525252] text-[15px] leading-relaxed mb-6">
                  {s.desc}
                </p>

                <ul className="space-y-2 mb-6">
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#171717] flex items-center gap-3"
                    >
                      <span className="w-2 h-2 bg-[#0047FF]" />
                      {b}
                    </li>
                  ))}
                </ul>

                {s.image && (
                  <div className="mt-auto zoom-wrap overflow-hidden border-t border-[#E5E5E5] -mx-8 md:-mx-10 -mb-8 md:-mb-10">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="zoom-img w-full h-44 md:h-56 object-cover"
                    />
                  </div>
                )}

                {!s.image && (
                  <a
                    href="#contacto"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector("#contacto")?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="mt-auto inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#0047FF] hover:gap-3 transition-all"
                  >
                    Conversemos sobre tu proyecto <ArrowUpRight size={14} />
                  </a>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
